import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A 20 second voice note (Section 7). The crew would rather talk than type.
 *
 * MediaRecorder gives webm on Android Chrome and mp4 on iOS Safari. The blob
 * goes into the same local queue as a photo, so it uploads by itself when the
 * signal allows and never blocks the report.
 */

export const MAX_VOICE_SECONDS = 20;

export function extensionForMime(mime: string): string {
  if (/mp4|aac|m4a/i.test(mime)) return 'm4a';
  if (/ogg/i.test(mime)) return 'ogg';
  if (/mpeg|mp3/i.test(mime)) return 'mp3';
  return 'webm';
}

export function voiceSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
}

export type VoiceState = 'idle' | 'recording' | 'ready' | 'denied' | 'unsupported';

export function useVoiceNote() {
  const [state, setState] = useState<VoiceState>(voiceSupported() ? 'idle' : 'unsupported');
  const [seconds, setSeconds] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const timer = useRef<number | null>(null);

  const cleanup = useCallback(() => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    stream.current?.getTracks().forEach((track) => track.stop());
    stream.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const stop = useCallback(() => {
    if (recorder.current && recorder.current.state !== 'inactive') recorder.current.stop();
  }, []);

  const start = useCallback(async () => {
    if (!voiceSupported()) {
      setState('unsupported');
      return;
    }
    try {
      const media = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.current = media;
      const chunks: Blob[] = [];
      const rec = new MediaRecorder(media);
      recorder.current = rec;
      rec.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      rec.onstop = () => {
        cleanup();
        const type = rec.mimeType || chunks[0]?.type || 'audio/webm';
        setBlob(chunks.length ? new Blob(chunks, { type }) : null);
        setState(chunks.length ? 'ready' : 'idle');
      };
      rec.start();
      setSeconds(0);
      setBlob(null);
      setState('recording');
      timer.current = window.setInterval(() => {
        setSeconds((s) => {
          if (s + 1 >= MAX_VOICE_SECONDS) stop();
          return s + 1;
        });
      }, 1000);
    } catch {
      cleanup();
      setState('denied');
    }
  }, [cleanup, stop]);

  const discard = useCallback(() => {
    setBlob(null);
    setSeconds(0);
    setState(voiceSupported() ? 'idle' : 'unsupported');
  }, []);

  return { state, seconds, blob, start, stop, discard };
}
